import { createContext, useContext } from "react";
import type { CurrentUser } from "../../auth/useCurrentUser";
import type { RecentClient } from "../../nav/consultantNav";
import type { ClientLite, OrgSummary, PortfolioSummary } from "./types";
import type { Scope } from "./workspace";

// ── Consultant shell state ────────────────────────────────────────────────
// One hoisted store for everything the consultant workspace, the client
// management view and the client read-through share: the light client list
// (context block, switcher, palette), the portfolio (tabs, self-org), recent
// clients, the role switches and the invite / set-up dialogs. Provided by
// ConsultantPortfolioPage; it stays mounted across every /consultant/* route.

export interface PortfolioState {
  orgs: OrgSummary[];
  summary: PortfolioSummary | null;
  selfOrgId: string | null;
  /** True while a (re)load is in flight. */
  loading: boolean;
  /** True once the first load has settled (success or failure); never reset by a reload. */
  loaded: boolean;
  error: string;
}

export interface ConsultantShellValue {
  user: CurrentUser;
  permissions: string[];

  /** GET /consultant/clients (light list). Empty until loaded. */
  clients: ClientLite[];
  clientsLoaded: boolean;
  reloadClients: () => void;

  /** GET /consultant/portfolio. */
  portfolio: PortfolioState;
  reloadPortfolio: () => void;
  /** Drops an organisation from the loaded portfolio and client list (after DELETE). */
  forgetClient: (orgId: string) => void;
  /** After PUT /consultant/self-org. */
  setSelfOrgId: (orgId: string | null) => void;
  /** The consultancy's own organisation, once the portfolio has resolved it. */
  selfOrg: { id: string; name: string } | null;

  /**
   * The last five clients opened, resolved from the current session's client
   * list (only ids are persisted, per user - see nav/consultantNav.ts).
   */
  recentClients: RecentClient[];
  recordRecentClient: (orgId: string) => void;

  /**
   * "My clients" or "Whole firm" — which clients Today, the badge and the portfolio
   * pages lead with. Saved per person on the server (GET/PATCH /consultant/workspace/prefs).
   */
  scope: Scope;
  setScope: (scope: Scope) => void;
  /** firm:admin — assigns clients, sees firm revenue and colleagues' utilisation. */
  isFirmAdmin: boolean;

  /** Switches this tab to a client session on the consultancy's own organisation; false when the switch failed. */
  enterOwnOrgPortal: () => Promise<boolean>;
  /** Switches this tab to the auditor portal (bureau owned by the consultancy). */
  openAuditorPortal: () => Promise<void>;
  openSelfOrgSetup: () => void;
  openInviteClient: () => void;
  openInviteStaff: () => void;
  /** Shows a dismissible notice at the bottom of the screen. */
  notify: (message: string) => void;
}

export const ConsultantShellContext = createContext<ConsultantShellValue | null>(null);

export function useConsultantShell(): ConsultantShellValue {
  const value = useContext(ConsultantShellContext);
  if (!value) throw new Error("useConsultantShell must be used inside ConsultantPortfolioPage");
  return value;
}
